import React, { useEffect, useState } from 'react';
import { adminMiradorService, type MiradorClientItem, type SchemaCoverage } from '../../services/adminMiradorService';
import { Modal } from '../../components/ui';

interface Props {
  tenants: MiradorClientItem[];
}

interface Row {
  client_id: string;
  client_slug: string;
  client_name: string;
  domain: string;
  coverage: SchemaCoverage | null;
  error: string | null;
}

export const SchemaCoverageSection: React.FC<Props> = ({ tenants }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Row | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (tenants.length === 0) {
        setRows([]);
        return;
      }
      setLoading(true);
      const all: Row[] = [];
      for (const t of tenants) {
        // PROD1.8 v2 : push par domaine. On liste les domaines configurés
        // (`tenant.domains` dans la nouvelle structure) activés uniquement.
        const domains: string[] = Object.entries(t.domains || {})
          .filter(([, entry]) => entry.enabled)
          .map(([code]) => code);
        for (const d of domains) {
          let cov: SchemaCoverage | null = null;
          let err: string | null = null;
          try {
            cov = await adminMiradorService.schemaCoverage(t.client_id, d);
          } catch (e: any) {
            err = e?.message || 'erreur';
          }
          all.push({
            client_id: t.client_id,
            client_slug: t.slug,
            client_name: t.name,
            domain: d,
            coverage: cov,
            error: err,
          });
        }
      }
      if (!cancelled) {
        setRows(all);
        setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [tenants]);

  if (tenants.length === 0) return null;

  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold mb-3">Couverture du schéma vs Mirador</h2>
      {loading && <div className="text-sm text-gray-500">Chargement…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-sm text-gray-500 italic">Aucune paire (tenant × domaine) à analyser.</div>
      )}
      {!loading && rows.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Tenant</th>
                <th className="text-left px-3 py-2">Domaine</th>
                <th className="text-left px-3 py-2">Schéma actif</th>
                <th className="text-left px-3 py-2">Couverture</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const cov = r.coverage;
                const total = cov ? cov.covered.length + cov.missing.length : 0;
                return (
                  <tr key={`${r.client_id}-${r.domain}`} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.client_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.client_slug}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.domain}</td>
                    <td className="px-3 py-2">{cov?.schema_version ?? '—'}</td>
                    <td className="px-3 py-2">
                      {r.error ? (
                        <span className="text-red-700 text-xs">{r.error}</span>
                      ) : cov ? (
                        <span className="inline-block text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">
                          {cov.covered.length}/{total} colonnes Mirador couvertes
                          {cov.extra_dokimo.length > 0 && (
                            <span className="ml-2 text-amber-700">+{cov.extra_dokimo.length} extra Dokimo</span>
                          )}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {cov && (
                        <button
                          type="button"
                          onClick={() => setSelected(r)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-100"
                        >
                          Détails
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && selected.coverage && (
        <Modal
          isOpen
          onClose={() => setSelected(null)}
          title={`Couverture ${selected.client_slug} / ${selected.domain} — schéma ${selected.coverage.schema_version}`}
          size="lg"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <ColumnList
              title="Couverts"
              tone="ok"
              items={selected.coverage.covered}
              hint="Champs du schéma poussés vers Mirador (mapping nom-à-nom)."
            />
            <ColumnList
              title="Manquants"
              tone="warn"
              items={selected.coverage.missing}
              hint="Colonnes Mirador attendues mais absentes du schéma actif (push = NULL)."
            />
            <ColumnList
              title="Extra Dokimo"
              tone="info"
              items={selected.coverage.extra_dokimo}
              hint="Champs extraits par Dokimo qui n'ont pas de colonne Mirador (perdus côté Mirador)."
            />
          </div>
        </Modal>
      )}
    </section>
  );
};

const ColumnList: React.FC<{ title: string; tone: 'ok' | 'warn' | 'info'; items: string[]; hint: string }> = ({
  title,
  tone,
  items,
  hint,
}) => {
  const toneClass =
    tone === 'ok'
      ? 'text-emerald-800 bg-emerald-50 border-emerald-200'
      : tone === 'warn'
      ? 'text-amber-900 bg-amber-50 border-amber-200'
      : 'text-blue-900 bg-blue-50 border-blue-200';
  return (
    <div>
      <div className={`text-xs font-semibold uppercase px-2 py-1 rounded border ${toneClass} mb-1`}>
        {title} ({items.length})
      </div>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>
      {items.length === 0 ? (
        <div className="text-xs text-gray-400 italic">—</div>
      ) : (
        <ul className="text-xs font-mono space-y-0.5 max-h-72 overflow-auto">
          {items.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SchemaCoverageSection;
